import React, { useState } from 'react';
import { PlusIcon, CalculatorIcon, PercentIcon, TableIcon, ArrowDownIcon, ArrowUpIcon, ClockIcon, ScaleIcon, BanknoteIcon, LayersIcon, AlertCircleIcon, InfoIcon, Settings2Icon, SlidersIcon } from 'lucide-react';
export const CalculationRulesSettings = () => {
  // State for managing active sections
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  return <div className="space-y-8" data-id="element-420">
      {/* Ad Valorem Tax Rate Tables */}
      <div className="border-b border-gray-200 pb-8" data-id="element-421">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-422">
          Ad Valorem Tax Rate Tables
        </h3>
        <div className="space-y-4" data-id="element-423">
          {[{
          jurisdiction: 'Oromia',
          type: 'Residential',
          bands: [{
            min: '0',
            max: '500,000',
            rate: '0.1%'
          }, {
            min: '500,001',
            max: '1,000,000',
            rate: '0.2%'
          }, {
            min: '1,000,001',
            max: 'Above',
            rate: '0.3%'
          }]
        }, {
          jurisdiction: 'Amhara',
          type: 'Commercial',
          bands: [{
            min: '0',
            max: '1,000,000',
            rate: '0.3%'
          }, {
            min: '1,000,001',
            max: '5,000,000',
            rate: '0.4%'
          }, {
            min: '5,000,001',
            max: 'Above',
            rate: '0.5%'
          }]
        }].map(table => <div key={`${table.jurisdiction}-${table.type}`} className="border border-gray-200 rounded-lg p-4" data-id="element-424">
              <div className="flex justify-between items-center mb-4" data-id="element-425">
                <div data-id="element-426">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-427">
                    {table.jurisdiction} - {table.type}
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-428">
                    Effective: Jan 1, 2024
                  </p>
                </div>
                <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-429">
                  Edit Rates
                </button>
              </div>
              <div className="overflow-x-auto" data-id="element-430">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-431">
                  <thead className="bg-gray-50" data-id="element-432">
                    <tr data-id="element-433">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500" data-id="element-434">
                        Value Range (ETB)
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500" data-id="element-435">
                        Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white" data-id="element-436">
                    {table.bands.map(band => <tr key={band.min} data-id="element-437">
                        <td className="px-3 py-2 text-xs text-gray-900" data-id="element-438">
                          {band.min} - {band.max}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 text-right" data-id="element-439">
                          {band.rate}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>)}
          <button className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700" data-id="element-440">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-441" />
            Add Rate Table
          </button>
        </div>
      </div>
      {/* Service Fee/Tariff Tables */}
      <div className="border-b border-gray-200 pb-8" data-id="element-442">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-443">
          Service Fee & Tariff Configuration
        </h3>
        <div className="space-y-4" data-id="element-444">
          {[{
          service: 'Waste Collection',
          type: 'Residential',
          feeType: 'Fixed + Volume Based',
          rates: [{
            component: 'Base Fee',
            value: '100 ETB/month'
          }, {
            component: 'Volume',
            value: '50 ETB/bin'
          }]
        }, {
          service: 'Water Supply',
          type: 'Commercial',
          feeType: 'Tiered Consumption',
          rates: [{
            component: '0-10m³',
            value: '15 ETB/m³'
          }, {
            component: '11-20m³',
            value: '20 ETB/m³'
          }, {
            component: '>20m³',
            value: '25 ETB/m³'
          }]
        }].map(fee => <div key={`${fee.service}-${fee.type}`} className="border border-gray-200 rounded-lg p-4" data-id="element-445">
              <div className="flex justify-between items-center mb-3" data-id="element-446">
                <div data-id="element-447">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-448">
                    {fee.service} - {fee.type}
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-449">{fee.feeType}</p>
                </div>
                <div className="flex space-x-2" data-id="element-450">
                  <button className="text-sm text-gray-500 hover:text-gray-700" data-id="element-451">
                    Configure
                  </button>
                  <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-452">
                    Edit Rates
                  </button>
                </div>
              </div>
              <div className="space-y-2" data-id="element-453">
                {fee.rates.map(rate => <div key={rate.component} className="flex justify-between text-sm" data-id="element-454">
                    <span className="text-gray-600" data-id="element-455">{rate.component}</span>
                    <span className="text-gray-900 font-medium" data-id="element-456">
                      {rate.value}
                    </span>
                  </div>)}
              </div>
            </div>)}
          <button className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700" data-id="element-457">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-458" />
            Add Fee Structure
          </button>
        </div>
      </div>
      {/* Conditional Rules & Adjustments */}
      <div className="border-b border-gray-200 pb-8" data-id="element-459">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-460">
          Conditional Rules & Adjustments
        </h3>
        <div className="space-y-6" data-id="element-461">
          {/* Conditional Fee Rules */}
          <div data-id="element-462">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-463">
              Conditional Fee Rules
            </label>
            <div className="space-y-3" data-id="element-464">
              {[{
              name: 'Early Payment Discount',
              condition: 'IF PaymentDate <= DueDate - 15 days',
              action: 'Apply 2% discount',
              status: 'Active'
            }, {
              name: 'High Value Property Surcharge',
              condition: 'IF PropertyValue > 10,000,000 ETB',
              action: 'Add 0.5% surcharge',
              status: 'Active'
            }].map(rule => <div key={rule.name} className="border border-gray-200 rounded-lg p-4" data-id="element-465">
                  <div className="flex justify-between items-center mb-2" data-id="element-466">
                    <span className="text-sm font-medium text-gray-900" data-id="element-467">
                      {rule.name}
                    </span>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full" data-id="element-468">
                      {rule.status}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600" data-id="element-469">
                    <p className="font-mono text-xs" data-id="element-470">{rule.condition}</p>
                    <p className="font-mono text-xs" data-id="element-471">{rule.action}</p>
                  </div>
                </div>)}
              <button className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700" data-id="element-472">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-473" />
                Add Rule
              </button>
            </div>
          </div>
          {/* Indexation Rules */}
          <div data-id="element-474">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-475">
              Indexation/Inflation Adjustment Rules
            </label>
            <div className="space-y-3" data-id="element-476">
              {[{
              type: 'Property Tax Rates',
              adjustment: 'Annual CPI-based',
              frequency: 'Yearly',
              nextUpdate: 'Jan 1, 2025'
            }, {
              type: 'Service Fees',
              adjustment: 'Semi-annual fixed 5%',
              frequency: '6 months',
              nextUpdate: 'Jul 1, 2024'
            }].map(rule => <div key={rule.type} className="border border-gray-200 rounded-lg p-4" data-id="element-477">
                  <div className="flex justify-between items-center mb-2" data-id="element-478">
                    <span className="text-sm font-medium text-gray-900" data-id="element-479">
                      {rule.type}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-480">
                      Next: {rule.nextUpdate}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-481">
                    <span className="text-gray-600" data-id="element-482">{rule.adjustment}</span>
                    <span className="text-gray-600" data-id="element-483">
                      Every {rule.frequency}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Penalty & Interest Rules */}
      <div className="border-b border-gray-200 pb-8" data-id="element-484">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-485">
          Penalty & Interest Configuration
        </h3>
        <div className="space-y-6" data-id="element-486">
          {/* Penalty Rules */}
          <div data-id="element-487">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-488">
              Penalty Rules
            </label>
            <div className="space-y-3" data-id="element-489">
              {[{
              type: 'Late Payment',
              rate: '2% per month',
              basis: 'Outstanding Amount',
              compounding: 'Monthly'
            }, {
              type: 'Non-Declaration',
              rate: '25% one-time',
              basis: 'Tax Amount',
              compounding: 'None'
            }].map(penalty => <div key={penalty.type} className="border border-gray-200 rounded-lg p-4" data-id="element-490">
                  <div className="flex justify-between items-center mb-2" data-id="element-491">
                    <span className="text-sm font-medium text-gray-900" data-id="element-492">
                      {penalty.type}
                    </span>
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full" data-id="element-493">
                      {penalty.rate}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600" data-id="element-494">
                    <span data-id="element-495">Basis: {penalty.basis}</span>
                    <span data-id="element-496">Compounding: {penalty.compounding}</span>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Interest Rules */}
          <div data-id="element-497">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-498">
              Interest Rules
            </label>
            <div className="space-y-3" data-id="element-499">
              {[{
              type: 'Property Tax Arrears',
              rate: '12% per annum',
              basis: 'Simple Interest',
              calculation: 'Daily'
            }, {
              type: 'Service Fee Arrears',
              rate: '9% per annum',
              basis: 'Compound Interest',
              calculation: 'Monthly'
            }].map(interest => <div key={interest.type} className="border border-gray-200 rounded-lg p-4" data-id="element-500">
                  <div className="flex justify-between items-center mb-2" data-id="element-501">
                    <span className="text-sm font-medium text-gray-900" data-id="element-502">
                      {interest.type}
                    </span>
                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full" data-id="element-503">
                      {interest.rate}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600" data-id="element-504">
                    <span data-id="element-505">{interest.basis}</span>
                    <span data-id="element-506">Calc: {interest.calculation}</span>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Exemption & Relief Rules */}
      <div className="border-b border-gray-200 pb-8" data-id="element-507">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-508">
          Exemption & Relief Configuration
        </h3>
        <div className="space-y-6" data-id="element-509">
          {/* Eligibility Criteria */}
          <div data-id="element-510">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-511">
              Eligibility Criteria
            </label>
            <div className="space-y-3" data-id="element-512">
              {[{
              type: 'Senior Citizen Relief',
              criteria: ['Age >= 65 years', 'Primary Residence', 'Income <= 50,000 ETB/year'],
              relief: '50% reduction'
            }, {
              type: 'Agricultural Land',
              criteria: ['Zoned Agricultural', 'Active Farming', 'Size >= 2 hectares'],
              relief: '75% reduction'
            }].map(exemption => <div key={exemption.type} className="border border-gray-200 rounded-lg p-4" data-id="element-513">
                  <div className="flex justify-between items-center mb-2" data-id="element-514">
                    <span className="text-sm font-medium text-gray-900" data-id="element-515">
                      {exemption.type}
                    </span>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full" data-id="element-516">
                      {exemption.relief}
                    </span>
                  </div>
                  <div className="space-y-1" data-id="element-517">
                    {exemption.criteria.map((criterion, idx) => <div key={idx} className="flex items-center text-sm text-gray-600" data-id="element-518">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2" data-id="element-519"></span>
                        {criterion}
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Stacking Rules */}
          <div data-id="element-520">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-521">
              Exemption Stacking Rules
            </label>
            <div className="space-y-3" data-id="element-522">
              {[{
              scenario: 'Multiple Category Eligibility',
              rule: 'Apply highest applicable exemption only',
              example: 'If eligible for both Senior (50%) and Disability (75%), apply 75%'
            }, {
              scenario: 'Mixed Use Property',
              rule: 'Apply proportionally to eligible portions',
              example: 'Agricultural exemption applies only to farmed portion'
            }].map(rule => <div key={rule.scenario} className="border border-gray-200 rounded-lg p-4" data-id="element-523">
                  <div className="mb-2" data-id="element-524">
                    <span className="text-sm font-medium text-gray-900" data-id="element-525">
                      {rule.scenario}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1" data-id="element-526">{rule.rule}</p>
                  <p className="text-xs text-gray-500 italic" data-id="element-527">{rule.example}</p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Payment & Credit Rules */}
      <div className="border-b border-gray-200 pb-8" data-id="element-528">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-529">
          Payment & Credit Rules
        </h3>
        <div className="space-y-6" data-id="element-530">
          {/* Payment Allocation */}
          <div data-id="element-531">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-532">
              Payment Allocation Strategy
            </label>
            <div className="space-y-3" data-id="element-533">
              {[{
              priority: 1,
              allocation: 'Interest & Penalties',
              rule: 'Oldest first'
            }, {
              priority: 2,
              allocation: 'Tax Principal',
              rule: 'Oldest first'
            }, {
              priority: 3,
              allocation: 'Service Fees',
              rule: 'Proportional distribution'
            }].map(strategy => <div key={strategy.priority} className="border border-gray-200 rounded-lg p-4" data-id="element-534">
                  <div className="flex items-center justify-between mb-2" data-id="element-535">
                    <span className="text-sm font-medium text-gray-900" data-id="element-536">
                      {strategy.priority}. {strategy.allocation}
                    </span>
                    <button className="text-sm text-gray-500 hover:text-gray-700" data-id="element-537">
                      Reorder
                    </button>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-538">{strategy.rule}</p>
                </div>)}
            </div>
          </div>
          {/* Credit Balance Rules */}
          <div data-id="element-539">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-540">
              Credit Balance Handling
            </label>
            <div className="space-y-3" data-id="element-541">
              {[{
              scenario: 'Excess Payment',
              action: 'Auto-apply to future bills',
              threshold: 'Up to 5,000 ETB'
            }, {
              scenario: 'Refund Request',
              action: 'Process if > 5,000 ETB',
              threshold: 'Within 30 days'
            }].map(rule => <div key={rule.scenario} className="border border-gray-200 rounded-lg p-4" data-id="element-542">
                  <div className="flex justify-between items-center mb-2" data-id="element-543">
                    <span className="text-sm font-medium text-gray-900" data-id="element-544">
                      {rule.scenario}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-545">
                      {rule.threshold}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-546">{rule.action}</p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Special Calculations */}
      <div className="pt-8" data-id="element-547">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-548">
          Special Calculations & Factors
        </h3>
        <div className="space-y-6" data-id="element-549">
          {/* Proration Rules */}
          <div data-id="element-550">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-551">
              Proration Rules
            </label>
            <div className="space-y-3" data-id="element-552">
              {[{
              scenario: 'Property Ownership Change',
              method: 'Daily proration',
              basis: '365 days'
            }, {
              scenario: 'Service Activation/Termination',
              method: 'Monthly proration',
              basis: 'Partial month = full month'
            }].map(rule => <div key={rule.scenario} className="border border-gray-200 rounded-lg p-4" data-id="element-553">
                  <div className="mb-2" data-id="element-554">
                    <span className="text-sm font-medium text-gray-900" data-id="element-555">
                      {rule.scenario}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600" data-id="element-556">
                    <span data-id="element-557">{rule.method}</span>
                    <span data-id="element-558">{rule.basis}</span>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Valuation Adjustments */}
          <div data-id="element-559">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-560">
              Valuation Adjustment Factors
            </label>
            <div className="space-y-3" data-id="element-561">
              {[{
              factor: 'Property Condition',
              adjustments: [{
                condition: 'Excellent',
                factor: '+10%'
              }, {
                condition: 'Poor',
                factor: '-15%'
              }]
            }, {
              factor: 'Location Premium',
              adjustments: [{
                condition: 'Central Business District',
                factor: '+25%'
              }, {
                condition: 'Industrial Zone',
                factor: '-10%'
              }]
            }].map(factor => <div key={factor.factor} className="border border-gray-200 rounded-lg p-4" data-id="element-562">
                  <div className="mb-2" data-id="element-563">
                    <span className="text-sm font-medium text-gray-900" data-id="element-564">
                      {factor.factor}
                    </span>
                  </div>
                  <div className="space-y-1" data-id="element-565">
                    {factor.adjustments.map((adj, idx) => <div key={idx} className="flex justify-between text-sm text-gray-600" data-id="element-566">
                        <span data-id="element-567">{adj.condition}</span>
                        <span className={adj.factor.startsWith('+') ? 'text-green-600' : 'text-red-600'} data-id="element-568">
                          {adj.factor}
                        </span>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Precision & Rounding */}
          <div data-id="element-569">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-570">
              Calculation Precision & Rounding
            </label>
            <div className="space-y-3" data-id="element-571">
              {[{
              calculation: 'Property Value',
              precision: 'Nearest 1,000 ETB',
              direction: 'Round up'
            }, {
              calculation: 'Tax Amount',
              precision: 'Nearest 1 ETB',
              direction: 'Round up'
            }, {
              calculation: 'Service Fees',
              precision: 'Nearest 0.5 ETB',
              direction: 'Round to nearest'
            }].map(rule => <div key={rule.calculation} className="border border-gray-200 rounded-lg p-4" data-id="element-572">
                  <div className="flex justify-between items-center mb-2" data-id="element-573">
                    <span className="text-sm font-medium text-gray-900" data-id="element-574">
                      {rule.calculation}
                    </span>
                    <span className="text-xs text-gray-500" data-id="element-575">
                      {rule.direction}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-576">
                    Precision: {rule.precision}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
};